import React, {
  Dispatch,
  createContext,
  SetStateAction,
  useContext,
  useLayoutEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import AppContext from "src/components/AppContext";

// Theme constants
const THEME_STORAGE_KEY = "dark";
const DARK_BG = "#000000";
const LIGHT_BG = "#edf1fc";

type ThemeContextType = {
  isDark: boolean;
  toggleTheme: () => void;
  showSearch: boolean;
  setShoweSearch: Dispatch<SetStateAction<boolean>>;
};

const ThemeContext = createContext<ThemeContextType>({
  isDark: false,
  toggleTheme: () => {},
  showSearch: true,
  setShoweSearch: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = (props: { children: React.ReactNode }) => {
  const context = useContext(AppContext);
  const { darkMode, setDarkMode } = context.state;
  const [showSearch, setShoweSearch] = useState(true);

  // Apply theme class and background BEFORE browser paints - prevents flash
  useLayoutEffect(() => {
    if (typeof window === "undefined" || darkMode === undefined) return;

    const root = window.document.documentElement;
    const bgColor = darkMode ? DARK_BG : LIGHT_BG;

    const updateDOM = () => {
      // Update theme class
      root.classList.toggle("dark", !!darkMode);

      // Update background colors for crucial nodes
      const body = window.document.body;
      const nextRoot = document.getElementById("__next");

      root.style.setProperty("background-color", bgColor, "important");
      if (body) {
        body.style.setProperty("background-color", bgColor, "important");
      }
      if (nextRoot) {
        nextRoot.style.setProperty("background-color", bgColor, "important");
      }
    };

    try {
      updateDOM();
      // Sync with localStorage only if value differs
      const shouldBeStored = darkMode ? "true" : "false";
      if (localStorage.getItem(THEME_STORAGE_KEY) !== shouldBeStored) {
        localStorage.setItem(THEME_STORAGE_KEY, shouldBeStored);
      }
    } catch (error) {
      console.error("Failed to update theme DOM:", error);
    }
  }, [darkMode]);

  const toggleTheme = useCallback(() => {
    setDarkMode?.(!darkMode);
  }, [darkMode, setDarkMode]);

  const value = useMemo(
    () => ({
      isDark: !!darkMode,
      toggleTheme,
      showSearch,
      setShoweSearch,
    }),
    [darkMode, toggleTheme, showSearch],
  );

  return (
    <ThemeContext.Provider value={value}>
      {props.children}
    </ThemeContext.Provider>
  );
};
